import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { isDataRow, Row } from '@paragon/layout-models';
import { forceArray } from '@paragon/utilities';

import { SelectableItemType } from '../../library/selection/types';
import { EventPosition } from '../../three/util/events/types';
import { sceneHoverCleared } from '../actions/sceneHoverActions';
import { cameraSlice } from './camera/cameraSlice';
import { interactiveEditingSlice } from './interactiveEditingSlice';

export interface Viewport {
  readonly hoveredItem: HoveredItem | null;
  readonly sceneTooltipOverride: string | null;
  readonly tooltipHidden: boolean;
  readonly rightClickedItem: RightClickedItem | null;

  // intended to be used similarly to `layout.highlightedPlane`; a way to draw attention to reference line(s) in the
  //   scene without actually selecting or marking them as hovered
  readonly highlightedReferenceLineGuids: { [guid: string]: true };
}

export interface HoveredItem {
  readonly guid: string;
  readonly type: SelectableItemType;
}

export const LAYOUT_RIGHT_CLICKABLE_ITEM_TYPES = [
  'beam',
  'ceilingPlane',
  'ceilingTray',
  'featurePatternContainer',
  'snappingUnderlay',
  'wall',
  'workLine',
  'roofContainer',
  'roofPlane',
  'trussEnvelope',

  // Classified as layout for now, maybe remove later
  'building',
  'level',
] as const;

export const ALL_RIGHT_CLICKABLE_ITEM_TYPES = [
  ...LAYOUT_RIGHT_CLICKABLE_ITEM_TYPES,
  'additionalLoads',
  'analogModel',
  'bearing',
  'building',
  'componentDesign',
  'featureArea',
  'hanger',
  'level',
  'member',
  'platePair',
  'project',
  'referenceLine',
  'trussGroup',
  'webSegment',
  'webJointSegment',
  null,
] satisfies (SelectableItemType | 'additionalLoads' | 'analogModel' | null)[];

export type RightClickableItemType = (typeof ALL_RIGHT_CLICKABLE_ITEM_TYPES)[number];

export const LAYOUT_RIGHT_CLICKABLE_CONTAINER_TYPES = [
  'beamContainer',
  'ceilingPlaneContainer',
  'ceilingTrayContainer',
  'featurePatternContainerContainer',
  'workLineContainer',
  'wallContainer',
  'roofPlaneContainer',
  'trussEnvelopeContainer',
] as const;

export const ALL_RIGHT_CLICKABLE_CONTAINER_TYPES = [
  ...LAYOUT_RIGHT_CLICKABLE_CONTAINER_TYPES,
  'bearingContainer',
  'braceContainer',
  'componentDesignContainer',
  'gussetContainer',
  'jackGirderConnectionContainer',
  'hangerContainer',
  'loadCaseContainer',
  'manualLoadContainer',
  'memberContainer',
  'movingLoadContainer',
  'parapetLoadContainer',
  'platePairContainer',
  'referenceLineContainer',
  'spliceContainer',
  'trussGroupContainer',
] as const;
export type RightClickableContainerType = (typeof ALL_RIGHT_CLICKABLE_CONTAINER_TYPES)[number];

export function rowIsRightClickable(row: Row): boolean {
  return isDataRow(row)
    ? isRightClickableItemType(row.type)
    : row.childrenType != null && isRightClickableItemContainerType(`${row.childrenType}Container`);
}

export function isRightClickableItemType(itemType: string | null): itemType is RightClickableItemType {
  return itemType != null && (ALL_RIGHT_CLICKABLE_ITEM_TYPES as string[]).includes(itemType);
}

export function isRightClickableItemContainerType(
  childrenType: string | null,
): childrenType is RightClickableContainerType {
  return childrenType != null && (ALL_RIGHT_CLICKABLE_CONTAINER_TYPES as readonly string[]).includes(childrenType);
}

export interface RightClickedItemPosition extends EventPosition {
  // If true, indicates that the action prompting the context menu happened outside the scene. This position will
  //   determine the menu position, but will not be useful for anything else.
  readonly outsideScene?: true;
}

export type RightClickedItemWithoutPosition =
  | { readonly type: null }
  | { readonly type: RightClickableContainerType }
  | { readonly type: Exclude<RightClickableItemType, 'referenceLine' | null>; readonly guid: string }
  | { readonly type: 'additionalLoads' | 'analogModel' }
  | { readonly type: 'referenceLine'; readonly guids: string[] };

export type RightClickedItem = RightClickedItemWithoutPosition & {
  readonly position: RightClickedItemPosition;
  requestedStartingReplacement?: string;
};

const initialState: Viewport = {
  hoveredItem: null,
  sceneTooltipOverride: null,
  tooltipHidden: false,
  rightClickedItem: null,

  highlightedReferenceLineGuids: {},
};

const viewportSlice = createSlice({
  name: 'viewport',
  initialState,
  reducers: {
    __UNSAFE_setHoverItem: (state, action: PayloadAction<HoveredItem>) => {
      state.hoveredItem = action.payload;
      state.sceneTooltipOverride = null;
    },
    setTooltip: (state, action: PayloadAction<string>) => {
      state.hoveredItem = null;
      state.sceneTooltipOverride = action.payload;
    },
    /** Consider `setTooltip` first. */
    setTooltipHidden_Internal: (state, action: PayloadAction<boolean>) => {
      state.tooltipHidden = action.payload;
    },
    clearHoverItem: (state) => {
      state.hoveredItem = null;
      state.sceneTooltipOverride = null;
    },
    setRightClickedItem: (state, action: PayloadAction<RightClickedItem>) => {
      state.rightClickedItem = castDraft(action.payload);
    },
    clearRightClickedItems: (state) => {
      state.rightClickedItem = null;
    },
    setHighlightedReferenceLineGuids: (state, action: PayloadAction<string | string[]>) => {
      state.highlightedReferenceLineGuids = Object.fromEntries(forceArray(action.payload).map((guid) => [guid, true]));
    },
    clearHighlightedReferenceLineGuids: (state) => {
      state.highlightedReferenceLineGuids = {};
    },
  },

  extraReducers: (builder) => {
    builder.addCase(sceneHoverCleared, (state) => {
      state.hoveredItem = null;
      state.sceneTooltipOverride = null;
    });
    builder.addCase(cameraSlice.actions.cameraAltered, (state) => {
      state.rightClickedItem = null;
    });
    builder.addMatcher(
      isAnyOf(
        interactiveEditingSlice.actions.__UNSAFE_startLengthEdit,
        interactiveEditingSlice.actions.__UNSAFE_startMoveEdit,
      ),
      (state) => {
        state.hoveredItem = null;
        state.sceneTooltipOverride = null;
      },
    );
  },
});

export const {
  __UNSAFE_setHoverItem,
  setTooltip,
  setTooltipHidden_Internal,
  clearHoverItem,
  setRightClickedItem,
  clearRightClickedItems,
  setHighlightedReferenceLineGuids,
  clearHighlightedReferenceLineGuids,
} = viewportSlice.actions;
export const viewport = viewportSlice.reducer;
